import { useState, useEffect, useCallback, useRef } from 'react'

interface LocationData {
  latitude: number
  longitude: number
  accuracy: number       // dokładność pozioma w metrach (im mniej tym lepiej)
  altitude: number | null
  altitudeAccuracy: number | null
  heading: number | null // kurs z GPS (° od N, zgodnie z ruchem wskazówek), gdy w ruchu
  speed: number | null   // prędkość m/s
  timestamp: number
}

interface OrientationData {
  alpha: number
  beta: number
  gamma: number
}

interface DeviceLocationState {
  location: LocationData | null
  orientation: OrientationData | null
  heading: number | null                 // najlepszy kurs: GPS w ruchu, inaczej kompas
  headingSource: 'gps' | 'compass' | null
  isSupported: boolean
  isMobile: boolean
  needsOrientationPermission: boolean     // iOS: trzeba poprosić o zgodę gestem
  error: string | null
}

// Wysoka dokładność = GPS; maximumAge:0 = zawsze świeży odczyt.
const GEO_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 25000,
  maximumAge: 0,
}

// Odległość Haversine w metrach
function distM(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const R = 6371000
  const dLat = ((bLat - aLat) * Math.PI) / 180
  const dLng = ((bLng - aLng) * Math.PI) / 180
  const s = Math.sin(dLat / 2) ** 2 +
    Math.cos((aLat * Math.PI) / 180) * Math.cos((bLat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)))
}

export function useDeviceLocation() {
  const [state, setState] = useState<DeviceLocationState>({
    location: null,
    orientation: null,
    heading: null,
    headingSource: null,
    isSupported: false,
    isMobile: false,
    needsOrientationPermission: false,
    error: null,
  })

  const watchIdRef = useRef<number | null>(null)
  const lastRef = useRef<LocationData | null>(null)
  const wakeLockRef = useRef<any>(null)
  const compassHeadingRef = useRef<number | null>(null)

  const checkIsMobile = useCallback(() => {
    const ua = navigator.userAgent.toLowerCase()
    const kw = ['mobile', 'android', 'iphone', 'ipad', 'ipod', 'blackberry', 'windows phone']
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0
    return kw.some((k) => ua.includes(k)) || hasTouch
  }, [])

  useEffect(() => {
    const geoSupported = 'geolocation' in navigator
    // iOS 13+ wymaga jawnej zgody na orientację (kompas) przez gest użytkownika
    const iosNeedsPerm = typeof (DeviceOrientationEvent as any)?.requestPermission === 'function'
    setState((p) => ({ ...p, isSupported: geoSupported, isMobile: checkIsMobile(), needsOrientationPermission: iosNeedsPerm }))
  }, [checkIsMobile])

  const handleError = useCallback((error: GeolocationPositionError) => {
    let msg = 'Nie udało się uzyskać lokalizacji'
    if (error.code === error.PERMISSION_DENIED) msg = 'Brak uprawnień do lokalizacji'
    else if (error.code === error.POSITION_UNAVAILABLE) msg = 'Lokalizacja niedostępna (brak sygnału GPS)'
    else if (error.code === error.TIMEOUT) msg = 'Przekroczono czas oczekiwania na lokalizację'
    setState((p) => ({ ...p, error: msg }))
  }, [])

  // Łączenie kursu: GPS gdy realnie się poruszamy, inaczej kompas.
  const bestHeading = useCallback((gpsHeading: number | null, speed: number | null): { heading: number | null; source: 'gps' | 'compass' | null } => {
    if (gpsHeading != null && !Number.isNaN(gpsHeading) && (speed ?? 0) > 0.6) {
      return { heading: gpsHeading, source: 'gps' }
    }
    if (compassHeadingRef.current != null) return { heading: compassHeadingRef.current, source: 'compass' }
    if (gpsHeading != null && !Number.isNaN(gpsHeading)) return { heading: gpsHeading, source: 'gps' }
    return { heading: null, source: null }
  }, [])

  const onPosition = useCallback((pos: GeolocationPosition) => {
    const c = pos.coords
    const next: LocationData = {
      latitude: c.latitude,
      longitude: c.longitude,
      accuracy: c.accuracy,
      altitude: c.altitude ?? null,
      altitudeAccuracy: c.altitudeAccuracy ?? null,
      heading: c.heading != null && !Number.isNaN(c.heading) ? c.heading : null,
      speed: c.speed != null && !Number.isNaN(c.speed) ? c.speed : null,
      timestamp: pos.timestamp,
    }

    // Inteligentna aktualizacja: akceptuj gdy dokładniejszy fix, albo realny ruch,
    // albo poprzedni odczyt się zestarzał (żeby okrąg dokładności nie "utknął").
    const prev = lastRef.current
    const moved = prev ? distM(prev.latitude, prev.longitude, next.latitude, next.longitude) : Infinity
    const stale = prev ? pos.timestamp - prev.timestamp > 8000 : true
    const accept = !prev || next.accuracy <= prev.accuracy || moved > Math.max(next.accuracy, 4) || stale
    if (!accept) return

    lastRef.current = next
    const { heading, source } = bestHeading(next.heading, next.speed)
    setState((p) => ({ ...p, location: next, heading, headingSource: source, error: null }))
  }, [bestHeading])

  const acquireWakeLock = useCallback(async () => {
    try {
      const wl = (navigator as any).wakeLock
      if (wl && !wakeLockRef.current) {
        wakeLockRef.current = await wl.request('screen')
        wakeLockRef.current?.addEventListener?.('release', () => { wakeLockRef.current = null })
      }
    } catch { /* brak wsparcia / nie w foreground - ignorujemy */ }
  }, [])

  const releaseWakeLock = useCallback(() => {
    try { wakeLockRef.current?.release?.() } catch { /* ignore */ }
    wakeLockRef.current = null
  }, [])

  // Ciągły, wysokodokładny watch - utrzymuje GPS "rozgrzany" (najlepsza dokładność
  // na żywo). Brak cyklicznego zatrzymywania = brak ponownej, zgrubnej akwizycji.
  const startTracking = useCallback(() => {
    if (!('geolocation' in navigator)) {
      setState((p) => ({ ...p, error: 'Geolokalizacja nie jest wspierana na tym urządzeniu' }))
      return
    }
    if (watchIdRef.current !== null) return
    lastRef.current = null
    watchIdRef.current = navigator.geolocation.watchPosition(onPosition, handleError, GEO_OPTIONS)
    acquireWakeLock()
  }, [onPosition, handleError, acquireWakeLock])

  const stopTracking = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current)
      watchIdRef.current = null
    }
    releaseWakeLock()
  }, [releaseWakeLock])

  const getCurrentLocation = useCallback(() => {
    if (!('geolocation' in navigator)) return
    navigator.geolocation.getCurrentPosition(onPosition, handleError, GEO_OPTIONS)
  }, [onPosition, handleError])

  // Kompas: instaluje nasłuch orientacji (po ew. zgodzie na iOS)
  const installOrientationListener = useCallback(() => {
    const onAbsolute = (e: any) => {
      let hdg: number | null = null
      if (typeof e.webkitCompassHeading === 'number') {
        hdg = e.webkitCompassHeading // iOS: już ° od N zgodnie z ruchem wskazówek
      } else if (e.absolute && typeof e.alpha === 'number') {
        hdg = (360 - e.alpha) % 360  // Android/Chrome (absolute): przelicz na ° od N
      }
      if (hdg != null && !Number.isNaN(hdg)) compassHeadingRef.current = hdg
      if (e.alpha != null && e.beta != null && e.gamma != null) {
        setState((p) => ({ ...p, orientation: { alpha: e.alpha, beta: e.beta, gamma: e.gamma } }))
      }
    }
    window.addEventListener('deviceorientationabsolute', onAbsolute as any, true)
    window.addEventListener('deviceorientation', onAbsolute as any, true)
    return () => {
      window.removeEventListener('deviceorientationabsolute', onAbsolute as any, true)
      window.removeEventListener('deviceorientation', onAbsolute as any, true)
    }
  }, [])

  // Zgoda na kompas (iOS 13+). Wywołać z gestu użytkownika (klik).
  const requestOrientationPermission = useCallback(async (): Promise<boolean> => {
    try {
      const anyEvt = DeviceOrientationEvent as any
      if (typeof anyEvt?.requestPermission === 'function') {
        const res = await anyEvt.requestPermission()
        const granted = res === 'granted'
        setState((p) => ({ ...p, needsOrientationPermission: !granted }))
        return granted
      }
      return true
    } catch {
      return false
    }
  }, [])

  // Nasłuch orientacji: na urządzeniach bez wymogu zgody (Android) od razu;
  // na iOS dopiero po requestOrientationPermission (ale listener i tak nie zaszkodzi).
  useEffect(() => {
    const cleanup = installOrientationListener()
    return cleanup
  }, [installOrientationListener])

  // Ponowne pozyskanie wake locka po powrocie do karty
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'visible' && watchIdRef.current !== null) acquireWakeLock() }
    document.addEventListener('visibilitychange', onVis)
    return () => document.removeEventListener('visibilitychange', onVis)
  }, [acquireWakeLock])

  useEffect(() => () => stopTracking(), [stopTracking])

  return {
    ...state,
    getCurrentLocation,
    startTracking,
    stopTracking,
    requestOrientationPermission,
  }
}
